import { useState, useMemo, useCallback } from 'react'
import {
  Block,
  BlockTitle,
  List,
  ListItem,
  Segmented,
  SegmentedButton,
  Sheet,
  Button,
  Toast,
  Toolbar,
} from 'konsta/react'
import {
  ArrowUpRight,
  ArrowDownLeft,
  Gamepad2,
  Trophy,
  Wallet,
  X,
  Copy,
  Clock,
  Hash,
  CreditCard,
  FileText,
  CheckCircle2,
  CircleDashed,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { formatCurrency, formatRelativeTime, cn } from '@/lib/utils'

// --- Types ---

interface Transaction {
  id: string
  type: 'deposit' | 'withdraw' | 'game' | 'jackpot'
  amount: number
  description: string
  date: Date
  status: string
  method?: string
}

// --- Mock Data ---

const mockTransactions: Transaction[] = [
  { id: '1', type: 'deposit', amount: 10000, description: 'USDT 儲值', date: new Date(Date.now() - 1000 * 60 * 30), status: '完成', method: 'USDT TRC-20' },
  { id: '2', type: 'game', amount: -4520, description: '新手桌 #1 - 輸', date: new Date(Date.now() - 1000 * 60 * 60 * 2), status: '完成' },
  { id: '3', type: 'game', amount: 8750, description: '中級桌 #3 - 贏', date: new Date(Date.now() - 1000 * 60 * 60 * 5), status: '完成' },
  { id: '4', type: 'withdraw', amount: -5000, description: '提款至 USDT', date: new Date(Date.now() - 1000 * 60 * 60 * 24), status: '處理中', method: 'USDT TRC-20' },
  { id: '5', type: 'deposit', amount: 25000, description: 'USDT 儲值', date: new Date(Date.now() - 1000 * 60 * 60 * 48), status: '完成', method: 'USDT TRC-20' },
  { id: '6', type: 'game', amount: 3200, description: '新手桌 #2 - 贏', date: new Date(Date.now() - 1000 * 60 * 60 * 72), status: '完成' },
  { id: '7', type: 'jackpot', amount: 17777, description: '累積獎金 - 皇家同花順', date: new Date(Date.now() - 1000 * 60 * 60 * 96), status: '完成' },
  { id: '8', type: 'withdraw', amount: -20000, description: '提款至 USDT', date: new Date(Date.now() - 1000 * 60 * 60 * 120), status: '完成', method: 'USDT TRC-20' },
]

// --- Tab definitions ---

type TabKey = 'all' | 'deposit' | 'withdraw' | 'game'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'deposit', label: '儲值' },
  { key: 'withdraw', label: '提款' },
  { key: 'game', label: '遊戲' },
]

// --- Helpers ---

function getTransactionIcon(type: Transaction['type']) {
  switch (type) {
    case 'deposit':
      return { Icon: ArrowDownLeft, color: '#34C759', bg: 'bg-[#34C759]/15' }
    case 'withdraw':
      return { Icon: ArrowUpRight, color: '#FF3B30', bg: 'bg-[#FF3B30]/15' }
    case 'game':
      return { Icon: Gamepad2, color: '#AF52DE', bg: 'bg-[#AF52DE]/15' }
    case 'jackpot':
      return { Icon: Trophy, color: '#FF9500', bg: 'bg-[#FF9500]/15' }
  }
}

function TransactionMediaIcon({ type }: { type: Transaction['type'] }) {
  const config = getTransactionIcon(type)
  const { Icon } = config
  return (
    <div
      className={cn(
        'w-10 h-10 rounded-xl flex items-center justify-center',
        config.bg,
      )}
    >
      <Icon className="w-5 h-5" style={{ color: config.color }} />
    </div>
  )
}

function formatAmount(cents: number): string {
  const abs = Math.abs(cents)
  const formatted = formatCurrency(abs)
  return cents >= 0 ? `+${formatted}` : `-${formatted}`
}

function getAmountColor(cents: number): string {
  if (cents > 0) return '#34C759'
  if (cents < 0) return '#FF3B30'
  return '#8E8E93'
}

// --- Detail row helper ---

function DetailRow({
  icon: Icon,
  label,
  value,
  iconColor = '#8E8E93',
  valueColor,
}: {
  icon: React.ElementType
  label: string
  value: string
  iconColor?: string
  valueColor?: string
}) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-2.5 text-[#8E8E93]">
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
        <span className="text-[14px]">{label}</span>
      </div>
      <span
        className="text-[14px] font-medium"
        style={{ color: valueColor || '#000' }}
      >
        {value}
      </span>
    </div>
  )
}

// --- Component ---

export function WalletScreen() {
  const user = useAuthStore((s) => s.user)
  const balance = user?.balance ?? 0

  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [toastOpen, setToastOpen] = useState(false)

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    if (activeTab === 'all') return mockTransactions
    if (activeTab === 'game') {
      return mockTransactions.filter((t) => t.type === 'game' || t.type === 'jackpot')
    }
    return mockTransactions.filter((t) => t.type === activeTab)
  }, [activeTab])

  const handleTxTap = (tx: Transaction) => {
    setSelectedTx(tx)
    setSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setSheetOpen(false)
    setTimeout(() => setSelectedTx(null), 300)
  }

  const handleCopyId = useCallback(() => {
    if (selectedTx) {
      navigator.clipboard?.writeText(`TXN-${selectedTx.id.padStart(8, '0')}`)
      setToastOpen(true)
      setTimeout(() => setToastOpen(false), 2000)
    }
  }, [selectedTx])

  // Stats summary
  const stats = useMemo(() => {
    const deposits = mockTransactions
      .filter((t) => t.type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0)
    const withdrawals = mockTransactions
      .filter((t) => t.type === 'withdraw')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0)
    const gameNet = mockTransactions
      .filter((t) => t.type === 'game' || t.type === 'jackpot')
      .reduce((sum, t) => sum + t.amount, 0)
    return { deposits, withdrawals, gameNet }
  }, [])

  return (
    <>
      {/* Balance card */}
      <div className="px-4 pt-4 pb-2">
        <div className="bg-gradient-to-br from-[#007AFF] to-[#5856D6] rounded-2xl p-5 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 opacity-80" />
            <span className="text-[13px] opacity-80">可用餘額</span>
          </div>
          <p className="text-[36px] font-bold tracking-tight leading-tight">
            {formatCurrency(balance)}
          </p>

          {/* Quick stats */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/20">
            <div className="flex-1">
              <p className="text-[11px] opacity-60">總儲值</p>
              <p className="text-[14px] font-semibold">
                {formatCurrency(stats.deposits)}
              </p>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="flex-1">
              <p className="text-[11px] opacity-60">總提款</p>
              <p className="text-[14px] font-semibold">
                {formatCurrency(stats.withdrawals)}
              </p>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="flex-1">
              <p className="text-[11px] opacity-60">遊戲盈虧</p>
              <p
                className="text-[14px] font-semibold"
                style={{
                  color: stats.gameNet >= 0 ? '#A8F0B8' : '#FFB3B0',
                }}
              >
                {stats.gameNet >= 0 ? '+' : ''}{formatCurrency(Math.abs(stats.gameNet))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-4 py-3 flex gap-3">
        <Button
          large
          className="flex-1"
          onClick={() => {}}
        >
          <ArrowDownLeft className="w-4 h-4 mr-1.5 inline" />
          儲值
        </Button>
        <Button
          large
          outline
          className="flex-1"
          onClick={() => {}}
        >
          <ArrowUpRight className="w-4 h-4 mr-1.5 inline" />
          提款
        </Button>
      </div>

      {/* Segmented tabs */}
      <div className="px-4 py-1">
        <Segmented strong>
          {tabs.map((tab) => (
            <SegmentedButton
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </SegmentedButton>
          ))}
        </Segmented>
      </div>

      {/* Transaction count */}
      <BlockTitle className="!mt-2">
        交易紀錄 ({filteredTransactions.length})
      </BlockTitle>

      {/* Transaction list */}
      {filteredTransactions.length > 0 ? (
        <List strongIos insetIos>
          {filteredTransactions.map((tx) => (
            <ListItem
              key={tx.id}
              link
              chevron={false}
              onClick={() => handleTxTap(tx)}
              media={<TransactionMediaIcon type={tx.type} />}
              title={
                <span className="font-medium">{tx.description}</span>
              }
              subtitle={
                <span className="text-[#8E8E93] text-[12px]">
                  {formatRelativeTime(tx.date)}
                  {tx.status === '處理中' && (
                    <span className="ml-1.5 text-[#FF9500]">
                      <CircleDashed className="w-3 h-3 inline -mt-0.5 mr-0.5" />
                      {tx.status}
                    </span>
                  )}
                </span>
              }
              after={
                <span
                  className="text-[15px] font-semibold tabular-nums"
                  style={{ color: getAmountColor(tx.amount) }}
                >
                  {formatAmount(tx.amount)}
                </span>
              }
            />
          ))}
        </List>
      ) : (
        <Block className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-[#C7C7CC] mb-3" />
          <p className="text-[#8E8E93] text-[15px]">
            沒有交易紀錄
          </p>
        </Block>
      )}

      {/* Bottom padding for tab bar */}
      <div className="h-8" />

      {/* Transaction Detail Sheet */}
      <Sheet
        opened={sheetOpen}
        onBackdropClick={handleCloseSheet}
        className="pb-safe"
      >
        {selectedTx && (
          <div className="flex flex-col">
            {/* Sheet header */}
            <Toolbar top>
              <div className="flex items-center justify-between w-full px-1">
                <span className="text-[17px] font-semibold">
                  交易詳情
                </span>
                <button
                  onClick={handleCloseSheet}
                  className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-[#8E8E93]" />
                </button>
              </div>
            </Toolbar>

            {/* Sheet content */}
            <div className="px-4 pt-2 pb-6">
              {/* Amount highlight */}
              <div className="text-center py-4">
                <div className="mb-2">
                  <TransactionMediaIcon type={selectedTx.type} />
                </div>
                <p
                  className="text-[32px] font-bold tabular-nums"
                  style={{ color: getAmountColor(selectedTx.amount) }}
                >
                  {formatAmount(selectedTx.amount)}
                </p>
                <p className="text-[14px] text-[#8E8E93] mt-1">
                  {selectedTx.description}
                </p>
              </div>

              {/* Details */}
              <div className="bg-[#F2F2F7] rounded-xl px-4 divide-y divide-black/5">
                <DetailRow
                  icon={Hash}
                  label="交易編號"
                  value={`TXN-${selectedTx.id.padStart(8, '0')}`}
                  iconColor="#007AFF"
                />
                <DetailRow
                  icon={Clock}
                  label="時間"
                  value={selectedTx.date.toLocaleString('zh-TW', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  iconColor="#8E8E93"
                />
                {selectedTx.method && (
                  <DetailRow
                    icon={CreditCard}
                    label="方式"
                    value={selectedTx.method}
                    iconColor="#AF52DE"
                  />
                )}
                <DetailRow
                  icon={CheckCircle2}
                  label="狀態"
                  value={selectedTx.status}
                  iconColor={selectedTx.status === '完成' ? '#34C759' : '#FF9500'}
                  valueColor={selectedTx.status === '完成' ? '#34C759' : '#FF9500'}
                />
              </div>

              {/* Copy button */}
              <div className="mt-4">
                <Button
                  large
                  outline
                  onClick={handleCopyId}
                >
                  <Copy className="w-4 h-4 mr-1.5 inline" />
                  複製交易編號
                </Button>
              </div>
            </div>
          </div>
        )}
      </Sheet>

      {/* Copy toast */}
      <Toast
        opened={toastOpen}
        position="center"
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
          <span>已複製到剪貼簿</span>
        </div>
      </Toast>
    </>
  )
}
